/* eslint-disable max-len */
// Packages
import moment from 'moment'
import validator from 'validator'
// Helpers
import { ApiService } from './api'

// Regex used in instructions and data.regex
// UPDATE SAME VARIABLES IN CYPRESS/CONSTANTS.JS
const legalNameRegex = '-.,\'"_/#& '
const shortenedNameRegex = '-\'& '
export const alphaNumericRegex = /^[a-zA-Z0-9]+$/;

export const ValidationMixin = {
  data () {
    return {
      instructions: {
        ACCOUNT_MANAGER_INSTRUCTION: 'Letters only',
        ACCOUNT_MANAGER_ID_INSTRUCTION: 'Please select an account manager',
        ACCOUNT_NUMBER: 'Numbers only',
        ADDRESS_INSTRUCTION: 'Letters, numbers, spaces, and `-._/#` only',
        ADDRESS_2_INSTRUCTION: 'Letters, numbers, spaces, and `-._/#` only',
        ADJUSTMENT_INSTRUCTION: 'Must be a number equal to or greater than 0',
        ADVANCE_INSTRUCTION: 'Must be empty or a number equal to or greater than 0',
        AUTHORITY_DATE_INSTRUCTION: 'Must be a date before today',
        BANK_NAME_INSTRUCTION: 'Letters, numbers, apostrophes, commas, periods, and spaces only',
        BANK_OWNER_NAME_INSTRUCTION: 'Letters and spaces only',
        BATCH_DATE_INSTRUCTION: 'Must not be in the future',
        BATCH_DATE_INSTRUCTION_LONG: 'Batch date must not be in the future',
        BUYOUT_DATE_INSTRUCTION: 'Buyout date must not be in the future',
        CITY_INSTRUCTION: 'Letters and spaces only',
        CHECK_NUMBER_INSTRUCTION: 'Must be a number greater than 0',
        CHECK_NUMBER_INSTRUCTION_LONG: 'Check number must be a number greater than 0',
        CLIENT_INSTRUCTION: 'You must select a client',
        COLD_TRUCKS_INSTRUCTION: 'Must be a number equal to or greater than 0',
        CORPORATION_TYPE_INSTRUCTION: 'Must be \'llc\', \'corp\', or \'sole proprietor\'',
        COUNTRY_INSTRUCTION: 'Must select either US or CA',
        CREDIT_RATING_INSTRUCTION: 'Must be A, B, C, D, or F',
        CREDIT_RATING_REASON_INSTRUCTION: 'Must not be empty',
        DATE_INSTRUCTION: 'Must be a date before today',
        DATE_OF_BIRTH_INSTRUCTION: 'Must be a date before today',
        DAYS_OUTSTANDING_FROM_INSTRUCTION: 'Must be a number equal to or greater than 0',
        DAYS_OUTSTANDING_TO_INSTRUCTION: 'Must be a number equal to or greater than 0',
        // DBAs are names, so they much allow the same thing as any name type property
        DBA_CLIENT_INSTRUCTION: `1 DBA only. Letters, numbers, spaces, and ${legalNameRegex} only`,
        // Debtor DBAs can have more than one, so a comma is needed
        DBA_DEBTOR_INSTRUCTION: `Letters, numbers, spaces, and ${shortenedNameRegex} only`,
        DEBIT_AMOUNT_INSTRUCTION: 'Must be a number greater than 0',
        DEBTOR_INSTRUCTION: 'You must select a debtor',
        DETENTION_INSTRUCTION: 'Must be empty or a number equal to or greater than 0',
        DRIVER_NAME_INSTRUCTION: 'Letters and spaces only',
        DOLLAR_INSTRUCTION: 'Must be a number equal to or greater than 0',
        DOT_INSTRUCTION: 'Numbers only. Do not include "DOT."',
        DOT_VALID_INSTRUCTION: 'Please enter a valid DOT. Numbers and letters only. Do not include "DOT"',
        DOT_UNIQUE_INSTRUCTION: 'Dot must be unique',
        DRYVAN_TRUCKS_INSTRUCTION: 'Must be a number equal to or greater than 0',
        EIN_INSTRUCTION: 'Letters and numbers only',
        EIN_UNIQUE_INSTRUCTION: 'EIN must be unique and letters and numbers only',
        EMAIL_INSTRUCTION: 'Must be an email (`@`, domain name, ending required)',
        EMAIL_UNIQUE_INSTRUCTION: 'Must be a unique and valid email',
        EQUITY_INSTRUCTION: 'Must be a number (0 ≤ X ≥ 100)',
        END_DATE_INSTRUCTION: 'Date must be after the start date',
        EXTENSION_INSTRUCTION: 'Must be a number',
        EXTERNAL_DAYS_INSTRUCTION: 'Must be a number equal to or greater than 0', // 60, 90, total,
        EXTERNAL_INVOICES_INSTRUCTION: 'Must be a number equal to or greater than 0', // 60, 90, total
        FACTOR_RATE_PERCENTAGE_INSTRUCTION: 'Must be a number (0 ≤ X ≤ 5)',
        RESERVE_RATE_PERCENTAGE_INSTRUCTION: 'Must be a number (0 ≤ X ≤ 5)',
        CLIENT_LIMIT_INSTRUCTION: 'Limit should be greater than or equal to $2500 or empty',
        DEBOTOR_LIMIT_INSTRUCTION: 'Limit should be greater than $0 or empty',
        FAX_INSTRUCTION: 'Must be a 10 or 11-digit number ((), -, ext. ### allowed)',
        FLATBED_TRUCKS_INSTRUCTION: 'Must be a number equal to or greater than 0',
        HIGH_CREDIT_INSTRUCTION: 'Must be a number equal to or greater than 0',
        ID_INSTRUCTION: 'Must be a positive number',
        INSURANCE_COMPANY_NAME_INSTRUCTION: `Letters, spaces, and ${shortenedNameRegex} only`,
        INSURANCE_EFFECTIVE_DATE_INSTRUCTION: 'Must be a date before today',
        INSURANCE_PHONE_INSTRUCTION: 'Must be a 10 or 11-digit number ((), -, ext. ### allowed)',
        INSURANCE_PHONE_CONNECTED_INSTRUCTION: 'Must be a connected 10-digit number',
        INSURANCE_POLICY_NUMBER_INSTRUCTION: 'Must be a positive number',
        INSURANCE_RENEWAL_DATE_INSTRUCTION: 'Must be a date after today',
        INVOICE_DELIVERY_EMAILS_INSTRUCTION:
          'Each email must have a @, domain name, and ending and be separated by a comma',
        LANGUAGES_INSTRUCTION: 'Must be \'english\', \'punjabi\', or \'spanish\'',
        LEAD_ATTRIBUTION_INSTRUCTION: 'Must be \'referral\', \'web\', \'email\', or \'cold calling\'',
        LEASED_TRUCKS_INSTRUCTION: 'Must be a number equal to or greater than 0',
        LEGAL_NAME_INSTRUCTION: `Letters, numbers, spaces, and ${legalNameRegex} only`,
        LOAD_NUMBER_INSTRUCTION: 'Letters and numbers only. No spaces or -\'s.',
        LUMPER_INSTRUCTION: 'Must be empty or a number equal to or greater than 0',
        MC_INSTRUCTION: 'Numbers only. Do not include "MC."',
        MC_UNIQUE_INSTRUCTION: 'MC must be unique',
        MICRO_DEPOSIT_INSTRUCTION: 'Must be a decimal between 0 and 1',
        MONTHLY_PAYMENT_PER_TRUCK_INSTRUCTION: 'Must be a number greater than 0',
        NOA_EMAILS_INSTRUCTION: 'Each email must have a @, domain name, and ending and be separated by a comma',
        ONLINE_SUBMIT_URL_INSTRUCTION: 'Must be a valid URL',
        OTHER_TRUCKS_INSTRUCTION: 'Must be a number equal to or greater than 0',
        PASSWORD_INSTRUCTION: 'Must be 8+ characters long, contain at least 1 uppercase letter, number and special character',
        PASSWORD_VERIFICATION_INSTRUCTION: 'Must match password',
        PAYMENT_AMOUNT_INSTRUCTION: 'Must be a number greater than 0',
        PAYMENT_AMOUNT_INSTRUCTION_LONG: 'Payment amount must be a number greater than 0',
        PAY_STATUS_EMAILS_INSTRUCTION: 'Each email must have a @, domain name, and ending',
        PHONE_INSTRUCTION: 'Must be a 10 or 11-digit number ((), -, ext. ### allowed)',
        PHONE_CONNECTED_INSTRUCTION: 'Must be a connected number',
        PRIMARY_RATE_INSTRUCTION: 'Must be a number greater than 0',
        QUERY_INSTRUCTION: 'Letters and numbers only',
        RATING_REASON_INSTRUCTION: 'N/A',
        REFRIGERATED_TRUCKS_INSTRUCTION: 'Must be a number equal to or greater than 0',
        ROLE_INSTRUCTION: 'Letters, numbers, and spaces only',
        ROUTING_NUMBER_INSTRUCTION: 'Must be nine numbers long.',
        SALES_REP_ID_INSTRUCTION: 'Please select a sales manager',
        SHORTENED_NAME_INSTRUCTION: `Letters, numbers, spaces, and ${shortenedNameRegex} only`,
        START_DATE_INSTRUCTION: 'Date must not be in the future',
        STATE_INSTRUCTION: 'Please choose a state/province/territory',
        STEPDECK_TRUCKS_INSTRUCTION: 'Must be a number equal to or greater than 0',
        TRANSFER_DATE_INSTRUCTION: 'Date must not be in the future',
        UPLOAD_DEBTORS_DOWNLOAD_START_DATE_INSTRUCTION: 'Date must be before end date',
        YEAR_STARTED_INSTRUCTION: 'Must be a number greater than 0 and less than or equal to today\'s year',
        ZIP_CA_INSTRUCTION: 'Letters, numbers, and a space only. Example: H3X 5E0',
        ZIP_US_INSTRUCTION: 'Must be a 5-digit number or longer',
      },
      regex: {
        address: '-._/# ',
        checkNumber: '-',
        clientDBA: legalNameRegex,
        debtorDBA: shortenedNameRegex,
        legalName: legalNameRegex,
        shortenedName: shortenedNameRegex,
      },
    }
  },

  methods: {
    // NOTE NOTE NOTE
    // If you an input with JUST the blacklisted characters, it will return not valid, becuase
    // you're only passing an empty string to the validator function after blacklisting

    async checkPhoneNumber (phone, prefix = null) {
      if (!phone) {
        return false
      }
      try {
        return (
          await ApiService.checkPhoneConnected({ phone, prefix })
        ).data
      }
      catch (error) {
        this.captureSentryEvent(
          'validationMixin: checkPhoneNumber',
          {
            config: error.config,
            details: error,
            phone,
            prefix,
            response: error.response,
          }
        )
        this.CError(error)
        return false
      }
    },

    isValidAccountManagerId (id) {
      if (!id) {
        return false
      }
      return validator.isNumeric(id)
    },

    isValidAccountNumber (number) {
      if (!number) {
        return false
      }
      return validator.isNumeric(number)
    },

    isValidAddress (address) {
      if (!address) {
        return false
      }
      const blacklisted = validator.blacklist(address, this.regex.address)
      return validator.isAlphanumeric(blacklisted)
    },

    isValidAdjustment (adjustment, allowNegative = false) {
      // Adjustment is in string pennies
      if (!adjustment) {
        return false
      }
      // Since emittedInput.big value may be null due to a user typing two
      // "."'s (by design), we'll use .cleaned and not blacklist "." during validation
      const blacklisted = validator.blacklist(adjustment, ',')
      // 2147483648 is the max number sequelize can store
      const min = allowNegative
        ? -2147483648
        : 0
      return validator.isFloat(blacklisted, { min, max: 2147483648 })
    },

    isValidAdvance (advance) {
      // Advance is in string pennies
      if (!advance) {
        return false
      }
      // Since emittedInput.big value may be null due to a user typing two
      // "."'s (by design), we'll use .cleaned and not blacklist "." during validation
      const blacklisted = validator.blacklist(advance, ',')
      // 2147483648 is the max number sequelize can store
      return validator.isFloat(blacklisted, { min: 0, max: 2147483648 })
    },

    isValidAgencyName (name) {
      if (!name) {
        return false
      }
      const blacklisted = validator.blacklist(name, this.regex.shortenedName)
      return validator.isAlpha(blacklisted)
    },

    isValidAuthorityDate (date) {
      if (!date) {
        return false
      }
      return moment(date).isValid() && moment(date).isBefore(moment())
    },

    isValidBankName (name) {
      if (!name) {
        return false
      }
      const blacklisted = validator.blacklist(name, '\',. ')
      return validator.isAlphanumeric(blacklisted)
    },

    isValidBankOwner (name) {
      if (!name) {
        return false
      }
      const blacklisted = validator.blacklist(name, ' ')
      return validator.isAlpha(blacklisted)
    },

    isValidBatchDate (date) {
      if (!date) {
        return false
      }
      return moment(date).isValid() && moment(date).isBefore(moment())
    },

    isValidBuyoutDate (date) {
      if (!date) {
        return false
      }
      return moment(date).isValid() && moment(date).isBefore(moment())
    },

    isValidBusinessName (name) {
      if (!name) {
        return false
      }
      const blacklisted = validator.blacklist(name, this.regex.legalName)
      return validator.isAlphanumeric(blacklisted)
    },

    isValidCheckNumber (checkNumber) {
      if (!checkNumber) {
        return false
      }
      const blacklisted = validator.blacklist(checkNumber, this.regex.checkNumber)
      return validator.isInt(blacklisted, { gt: 0 })
    },

    isValidCity (city) {
      if (!city) {
        return false
      }
      const blacklisted = validator.blacklist(city, ' ')
      return validator.isAlpha(blacklisted)
    },

    isValidContactName (name) {
      if (!name) {
        return false
      }
      const blacklisted = validator.blacklist(name, this.regex.shortenedName)
      return validator.isAlpha(blacklisted)
    },

    isValidContactRole (role) {
      if (!role) {
        return false
      }
      const blacklisted = validator.blacklist(role, this.regex.shortenedName)
      return validator.isAlphanumeric(blacklisted)
    },

    isValidCorpType (type) {
      if (!type) {
        return false
      }
      const corpTypes = [ 'llc', 'corp', 'sole proprietor', 'partnership' ]
      return corpTypes.includes(type)
    },

    isValidCreditRating (rating) {
      if (!rating) {
        return false
      }
      const ratings = [ 'A', 'B', 'C', 'D', 'F' ]
      return ratings.includes(rating)
    },

    isValidCreditRatingReason (reason) {
      if (!reason) {
        return false
      }
      return reason.length
    },

    isValidDateOfBirth (date) {
      if (!date) {
        return false
      }
      return moment(date).isValid() && moment(date).isBefore(moment())
    },

    isValidDateRegistered (date) {
      if (!date) {
        return false
      }
      return moment(date).isValid()
    },

    isValidDaysOutstanding (days) {
      if (!days) {
        return false
      }
      return validator.isInt(days, { min: 0 })
    },

    isValidDBAClient (dba) {
      if (!dba) {
        return true
      }
      const blacklisted = validator.blacklist(dba.toLowerCase(), this.regex.clientDBA)
      return validator.isAlphanumeric(blacklisted)
    },

    isValidDBADebtor (dbas) {
      if (!dbas) {
        return true
      }
      const blacklisted = validator.blacklist(dbas.toLowerCase(), this.regex.debtorDBA)
      return validator.isAlphanumeric(blacklisted)
    },

    isValidDebitAmount (amount) {
      // Amount is in string pennies
      if (!amount) {
        return false
      }
      // Since emittedInput.big value may be null due to a user typing two
      // "."'s (by design), we'll use .cleaned and not blacklist "." during validation
      const blacklisted = validator.blacklist(amount, ',')
      // 2147483648 is the max number sequelize can store
      return validator.isFloat(blacklisted, { min: 0.01, max: 2147483648 })
    },

    isValidDeductionAmount (amount) {
      // Amount is in string pennies
      if (!amount) {
        return false
      }
      // Since emittedInput.big value may be null due to a user typing two
      // "."'s (by design), we'll use .cleaned and not blacklist "." during validation
      const blacklisted = validator.blacklist(amount, ',')
      // 2147483648 is the max number sequelize can store
      return validator.isFloat(blacklisted, { min: 0.01, max: 2147483648 })
    },

    isValidDetention (detention) {
      // Detention is in string pennies
      if (!detention) {
        return false
      }
      // Since emittedInput.big value may be null due to a user typing two
      // "."'s (by design), we'll use .cleaned and not blacklist "." during validation
      const blacklisted = validator.blacklist(detention, ',')
      // 2147483648 is the max number sequelize can store
      return validator.isFloat(blacklisted, { min: 0, max: 2147483648 })
    },

    isValidDriverName (name) {
      if (!name) {
        return false
      }
      const blacklisted = validator.blacklist(name, ' ')
      return validator.isAlpha(blacklisted)
    },

    isValidDOT (dot) {
      return alphaNumericRegex.test(dot);
    },

    isValidEffectiveDate (date) {
      if (!date) {
        return false
      }
      return moment(date).isValid() && moment(date).isBefore(moment())
    },

    isValidEIN (ein) {
      if (!ein) {
        return false
      }
      return validator.isAlphanumeric(ein)
    },

    isValidEmail (email) {
      if (!email) {
        return false
      }
      return validator.isEmail(email)
    },

    isValidEmails (emails) {
      if (!emails) {
        return false
      }
      let valid = true

      // Regardless of whether `emails` is a string or array, `.split(...)` works as intended
      emails.split(',').forEach((email) => {
        if (!this.isValidEmail(email.trim())) {
          valid = false
        }
      })

      return valid
    },

    isValidEndDate (startDate, endDate) {
      if (!startDate || !endDate) {
        return false
      }
      // As of 20200814, employees want to create reports for singular days
      return moment(endDate).isSameOrAfter(startDate)
    },

    isValidEquity (equity) {
      if (!equity) {
        return false
      }
      return validator.isFloat(equity, { min: 0.00, max: 100.00 })
    },

    isValidExtension (extension) {
      if (!extension) {
        return false
      }
      return validator.isNumeric(extension)
    },

    isValidExternal (external) {
      if (!external) {
        return false
      }
      return validator.isInt(external, { min: 0 })
    },

    isValidFactorRate (rate) {
      if (!rate) {
        return false
      }
      return validator.isFloat(rate, { min: 0.00, max: 5.00 })
    },

    isValidReserveRate (rate) {
      if (!rate) {
        return false
      }
      return validator.isFloat(rate, { min: 0.00, max: 5.00 })
    },

    isValidClientLimit (limit) {
      return !limit || validator.isFloat(limit, { min: 2500.00 })
    },

    isValidDebtorLimit (limit) {
      return !limit || validator.isFloat(limit, { min: 0.1 })
    },

    isValidFilename (filename) {
      if (!filename) {
        return false
      }
      return filename.length <= 100
    },

    isValidHighCredit (credit) {
      if (!credit) {
        return false
      }
      return validator.isInt(credit, { min: 0 })
    },

    isValidID (id) {
      if (!id) {
        return false
      }
      return validator.isInt(id, { min: 0 })
    },

    isValidInsuranceCompanyName (name) {
      if (!name) {
        return false
      }
      const blacklisted = validator.blacklist(name, this.regex.shortenedName)
      return validator.isAlpha(blacklisted)
    },

    isValidInsurancePolicyNumber (number) {
      if (!number) {
        return false
      }
      return validator.isInt(number, { gt: 0 })
    },

    isValidInsuranceRenewalDate (date) {
      if (!date) {
        return true
      }
      return moment(date).isValid() && moment(date).isAfter(moment())
    },

    isValidLanguage (languages) {
      if (!languages) {
        return false
      }
      const options = [ 'english', 'punjabi', 'spanish' ]
      return languages.length && languages.every((lang) => {
        return options.includes(lang)
      })
    },

    isValidLeadAttribution (attribution) {
      if (!attribution) {
        return false
      }
      const attributions = [ 'referral', 'web', 'email', 'cold calling' ]
      return attributions.includes(attribution)
    },

    isValidLegalName (name) {
      if (!name) {
        return false
      }
      const blacklisted = validator.blacklist(name, this.regex.legalName)
      return validator.isAlphanumeric(blacklisted)
    },

    isValidLoadNumber (loadNumber, allowDash = false) {
      if (!loadNumber) {
        return false
      }
      return validator.isAlphanumeric(
        allowDash
          ? loadNumber.replace(/-/g, '')
          : loadNumber
      )
    },

    isValidLumper (lumper) {
      // Lumper is in string pennies
      if (!lumper) {
        return false
      }
      // Since emittedInput.big value may be null due to a user typing two
      // "."'s (by design), we'll use .cleaned and not blacklist "." during validation
      const blacklisted = validator.blacklist(lumper, ',')
      // 2147483648 is the max number sequelize can store
      return validator.isFloat(blacklisted, { min: 0, max: 2147483648 })
    },

    isValidMC (mc) {
      if (!mc) {
        return false
      }
      return validator.isInt(mc, { gt: 9999 })
    },

    isValidMicroDeposit (deposit) {
      if (!deposit) {
        return false
      }
      // We fake the $0. for the user, so they're just entering the cents amount
      return validator.isInt(deposit, { gt: 0, lt: 100 })
    },

    isValidMonthlyPerTruck (payment) {
      if (!payment) {
        return false
      }
      return validator.isFloat(payment, { min: 0 })
    },

    isValidPassword (pass) {
      if (!pass) {
        return false
      }
      const specialCharRegex = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/
      let uppercaseLetterExists = false

      // Check for an uppercase letter
      pass.split('').forEach((ch) => {
        // Catches numbers
        if (!isNaN(ch * 1)) {
          return
        }
        // Catches special characters
        if (specialCharRegex.test(ch)) {
          return
        }
        if (ch === ch.toUpperCase()) {
          uppercaseLetterExists = true
        }
      })

      return (
        pass.length >= 8
        && pass.search(/([0-9.])/g) !== -1
        && specialCharRegex.test(pass)
        && uppercaseLetterExists
      )
    },

    isValidPayAmount (amount) {
      // Amount is in string pennies
      if (!amount) {
        return false
      }
      // Since emittedInput.big value may be null due to a user typing two
      // "."'s (by design), we'll use .cleaned and not blacklist "." during validation
      const blacklisted = validator.blacklist(amount, ',')
      // 2147483648 is the max number sequelize can store
      return validator.isFloat(blacklisted, { min: 0, max: 2147483648 })
    },

    isValidPhone (phone) {
      if (!phone) {
        return false
      }

      // phone (format: "(area)XXX-XXXX xEXT")
      /*
       * Covers the following use cases:
       * 123 456 7890
       * 123.456.7890
       * 123-456-7890
       * (123) 456 7890
       * (123)4567890
       * (123)-456-7890
       * (123).456.7890
       * 123456.7890
       * (123) 456.7890
       * (123)456-7890
       * x123
       * x123
       * [number] [ext]
       * But it also allows 123.456.7890 [e/x/t]123
       */
      // const arr = ['123 456 7890', '123.456.7890', '123-456-7890', '(123) 456 7890', '(123)4567890', '(123)-456-7890', '(123).456.7890', '123456.7890', '(123) 456.7890', '(123)456-7890', '123 456 7890 x123', '123.456.7890x123', '123-456-7890ext123', '(123) 456 7890 ext123', '(123)4567890 x 123', '(123)-456-7890 ext 123', '(123).456.7890x123', '123456.7890x123', '(123) 456.7890x123', '(123)456-7890']

      // Cleaned strips the input of any non-digit or x/X to get an accurate length
      const cleaned = (phone.trim()).replace(/[^0-9xX]/g, '')
      if (
        cleaned.length < 10
        || (cleaned.length > 11 && !cleaned.includes('x'))
      ) {
        return false
      }
      // eslint-disable-next-line max-len
      const phoneRegex = /^\s*(?:\+?(\d{1,3}))?([-.( ]*(\d{3})[-.) ]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/
      return phoneRegex.test(phone)
    },

    isValidPrimaryRate (rate) {
      // Rate is in string pennies
      if (!rate) {
        return false
      }
      // Since emittedInput.big value may be null due to a user typing two
      // "."'s (by design), we'll use .cleaned and not blacklist "." during validation
      const blacklisted = validator.blacklist(rate, ',')
      // 2147483648 is the max number sequelize can store
      return validator.isFloat(blacklisted, { min: 0, max: 2147483648 })
    },

    isValidProcessOfficeId (id) {
      if (!id) {
        return false
      }
      return id === '1' || id === '2'
    },

    isValidQuery (query) {
      if (!query) {
        return false
      }
      return validator.isAlphanumeric(query)
    },

    isValidRenewalDate (date) {
      if (!date) {
        return true
      }
      return moment(date).isValid()
    },

    isValidRole (role) {
      if (!role) {
        return false
      }
      const roles = [
        'Account Manager',
        'Underwriter',
        'Collections Specialist',
        'Processor',
        'Salesperson',
        'Master',
      ]
      return roles.includes(role)
    },

    isValidRoutingNumber (number) {
      if (!number) {
        return false
      }
      return validator.isInt(number) && (number).toString().length === 9
    },

    isValidShortenedName (name) {
      if (!name) {
        return false
      }
      // Currently, shortened name is being checked the same as client legal name
      // https://app.zenhub.com/workspaces/bobtail-5c64d9b0b66dba3bb32bda6e/issues/fs-bobtail/bobtail/1639
      const blacklisted = validator.blacklist(name, this.regex.shortenedName)
      return validator.isAlphanumeric(blacklisted)
    },

    isValidStartDate (startDate) {
      if (!startDate) {
        return false
      }
      return moment(startDate).isBefore(moment())
    },

    isValidToPage (toPage, pages) {
      if (!toPage) {
        return false
      }
      return validator.isNumeric(toPage)
        && toPage <= pages
        && toPage > 0
    },

    isValidTransferDate (date) {
      if (!date) {
        return false
      }
      return moment(date).isValid() && moment(date).isBefore(moment())
    },

    isValidTruckNumber (number) {
      if (!number) {
        return false
      }
      return validator.isInt(number, { min: 0 })
    },

    isValidUploadDebtorsDownloadStartDate (startDate, endDate) {
      if (!startDate || !endDate) {
        return false
      }
      return moment(startDate).isSameOrBefore(endDate)
    },

    isValidURL (url) {
      if (!url) {
        return false
      }
      return validator.isURL(url)
    },

    isValidYearStarted (year) {
      if (!year) {
        return false
      }
      return parseInt(year, 10) <= moment().format('YYYY')
    },

    isValidZipCA (zip) {
      if (!zip) {
        return false
      }
      return validator.isPostalCode(zip, 'CA')
    },

    isValidZipUS (zip) {
      if (!zip) {
        return false
      }
      return validator.isPostalCode(zip, 'US')
    },
  },
}
